import { useEffect, useRef, useState, type RefObject } from 'react';

export function useSectionInView(options?: IntersectionObserverInit): {
  ref: RefObject<HTMLElement | null>;
  inView: boolean;
} {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) setInView(true);
    }, options ?? { rootMargin: '120px 0px', threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);

  return { ref, inView };
}

export function useScrollSpy(sectionIds: string[], rootMargin = '-40% 0px -45% 0px') {
  const [active, setActive] = useState(sectionIds[0] ?? '');

  useEffect(() => {
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];
    if (!elements.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target.id) {
          setActive(visible[0].target.id);
        }
      },
      { rootMargin, threshold: [0, 0.1, 0.25] },
    );

    for (const el of elements) obs.observe(el);
    return () => obs.disconnect();
  }, [sectionIds, rootMargin]);

  return active;
}

export function scrollToSection(hash: string) {
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 112;
  window.scrollTo({ top, behavior: 'smooth' });
  history.replaceState(null, '', `#${id}`);
}
